import { useCallback } from 'react';

import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import { HighCostMedicationListImportStatus } from 'services/api/admin/highCostMedicationListImports';
import { HIGH_COST_MEDICATION_LIST_IMPORT_CHANNEL } from 'services/api/constants';

export type HighCostMedicationListImportChannelMessage = {
  id: string;
  status: HighCostMedicationListImportStatus;
  error: string | null;
  filename: string | null;
};

// ActionCable subscribe params bypass OliveBranch (which only runs as Rack middleware for HTTP), so
// the key must be snake_case to match what the Rails channel reads
// (high_cost_medication_list_import_id — see api/app/channels/application_cable/channel.rb).
type HighCostMedicationListImportChannelParams = {
  high_cost_medication_list_import_id?: string;
};

type UseHighCostMedicationListImportChannelOptions = {
  params: HighCostMedicationListImportChannelParams;
  onMessageReceived?: (data: HighCostMedicationListImportChannelMessage) => void;
};

export default function useHighCostMedicationListImportChannel({
  params,
  onMessageReceived,
}: UseHighCostMedicationListImportChannelOptions) {
  const handleReceived = useCallback(
    (data: HighCostMedicationListImportChannelMessage) => {
      onMessageReceived?.(data);
    },
    [onMessageReceived]
  );

  const { isConnected } = useWebsocketSubscription<HighCostMedicationListImportChannelMessage>({
    channel: HIGH_COST_MEDICATION_LIST_IMPORT_CHANNEL,
    params,
    onReceived: handleReceived,
    enabled: !!params.high_cost_medication_list_import_id,
  });

  return {
    isSubscribed: isConnected,
  };
}
